<!-- ActivityMonitoringSection.vue -->
<template>
  <div 
    v-if="(configType === 'collectorconfig') || (isStaff && configType === 'workspaceconfig')"  
    class="space-y-6 glass-panel dark:text-bg-gray"
  >
    <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
      Activity Monitoring
    </h2>

    <div 
      v-if="(!isGeneralFileUpload && configType === 'workspaceconfig') || (!isWorkspaceFileUpload && configType === 'collectorconfig')" 
      class="flex grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <!-- Activity Toggle -->
      <div class="flex flex-col">
        <label for="activity_bool" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Activity</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Master switch for user activity monitoring. Turning it off also turns off Content and Disclosed Mode
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.activity_bool"
            id="activity_bool"
            type="checkbox"
            @change="onActivityToggle"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="activity_bool"
            id="activity_bool_toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>

      <!-- Content Toggle -->
      <div class="flex flex-col">
        <label for="content_bool" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Content</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Enable or disable capturing of active window / content details (requires Activity)
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.content_bool"
            id="content_bool"
            type="checkbox"
            :disabled="!modelValue.activity_bool"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="content_bool"
            id="content_bool_toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>

      <!-- Disclosed Mode Toggle -->
      <div class="flex flex-col">
        <label for="disclosed_mode" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Disclosed Mode</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              When enabled the user is informed that activity is being monitored (requires Activity)
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.disclosed_mode"
            id="disclosed_mode"
            type="checkbox"
            :disabled="!modelValue.activity_bool"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="disclosed_mode"
            id="disclosed_mode_toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>

      <!-- Activity Interval (s) -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="activity_interval" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Activity Interval (s) <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Seconds between activity samples
            </div>
          </div>
        </label>
        <input
          v-model.number="modelValue.activity_interval"
          id="activity_interval"
          type="number"
          min="1"
          step="1"
          placeholder="30"
          @focus="emit('clear-error', 'activity_interval')"
          :class="[
            'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
            errors?.activity_interval
              ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.activity_interval"/>
      </div>

      <!-- Idle Threshold (s) -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="idle_threshold" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Idle Threshold (s) <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Seconds of inactivity before the user is considered idle
            </div>
          </div>
        </label>
        <input
          v-model.number="modelValue.idle_threshold"
          id="idle_threshold"
          type="number"
          min="1"
          step="1"
          placeholder="300"
          @focus="emit('clear-error', 'idle_threshold')"
          :class="[
            'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
            errors?.idle_threshold
              ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.idle_threshold"/>
      </div>
    </div>
  </div>
</template>

<script setup>
import FieldError from '../FieldError.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  configType: {
    type: String,
    required: true
  },
  isStaff: {
    type: Boolean,
    default: false
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  isWorkspaceFileUpload: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'clear-error'])

function onActivityToggle() {
  if (!props.modelValue.activity_bool) {
    props.modelValue.content_bool = false
    props.modelValue.disclosed_mode = false
  }
}
</script>
